<template>
  <NavBar pageName="GDPR Search"></NavBar>
  <v-container style="margin-top: 185px;">
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-shield-search</v-icon>
            GDPR Deleted Records Search
          </v-card-title>
          <v-card-subtitle class="pb-0">
            Search for GDPR-deleted users and clients by hashed personal data
          </v-card-subtitle>

          <v-card-text>
            <v-row align="center">
              <v-col cols="12" sm="3">
                <v-select
                  v-model="searchType"
                  :items="searchTypes"
                  item-title="text"
                  item-value="value"
                  label="Search field"
                  density="compact"
                  variant="outlined"
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="searchValue"
                  label="Search value (exact match, case insensitive)"
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  @keyup.enter="doSearch"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-btn
                  color="primary"
                  :loading="loading"
                  :disabled="!searchValue || searchValue.trim() === ''"
                  @click="doSearch"
                  block
                >
                  <v-icon class="mr-1">mdi-magnify</v-icon>
                  Search
                </v-btn>
              </v-col>
            </v-row>

            <v-alert
              v-if="searchPerformed && !loading && results.length === 0"
              type="info"
              variant="tonal"
              density="compact"
              class="mt-4"
            >
              No GDPR deleted records found for "{{ lastSearchValue }}" ({{ lastSearchType }})
            </v-alert>

            <v-alert
              v-if="errorMessage"
              type="error"
              variant="tonal"
              density="compact"
              class="mt-4"
              closable
              @click:close="errorMessage = ''"
            >
              {{ errorMessage }}
            </v-alert>

            <v-data-table
              v-if="results.length > 0"
              :headers="headers"
              :items="results"
              class="mt-4 elevation-1"
              density="compact"
              :items-per-page="25"
            >
              <template v-slot:[`item.entity_type`]="{ item }">
                <v-chip
                  :color="item.entity_type === 'Client' ? 'blue' : 'green'"
                  size="small"
                  variant="tonal"
                >
                  <v-icon size="small" class="mr-1">
                    {{ item.entity_type === 'Client' ? 'mdi-domain' : 'mdi-account' }}
                  </v-icon>
                  {{ item.entity_type }}
                </v-chip>
              </template>

              <template v-slot:[`item.deleted_at`]="{ item }">
                {{ formatDate(item.deleted_at) }}
              </template>

              <template v-slot:[`item.reason`]="{ item }">
                <span class="text-caption">{{ item.reason || '-' }}</span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <v-card class="mt-4" variant="outlined">
          <v-card-text>
            <v-icon class="mr-1" color="info">mdi-information-outline</v-icon>
            <strong>How it works:</strong> The search term is hashed with SHA256+salt and compared against stored hashes.
            You must enter the <strong>exact</strong> original value (name, email, username, or phone) to find a match.
            Partial matches are not possible due to one-way hashing.
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import store from "@/store";
import { searchGdprDeletions } from "@/composables/useGdprAPI";
import { log } from "@/composables/useLog";

export default defineComponent({
  name: 'GdprSearchSettings',
  components: {
    NavBar
  },
  data() {
    return {
      token: '',
      searchType: 'all',
      searchValue: '',
      lastSearchValue: '',
      lastSearchType: '',
      loading: false,
      searchPerformed: false,
      errorMessage: '',
      results: [],
      searchTypes: [
        { text: 'All fields', value: 'all' },
        { text: 'Name', value: 'name' },
        { text: 'Email', value: 'email' },
        { text: 'Username', value: 'username' },
        { text: 'Phone', value: 'phone' },
      ],
      headers: [
        { title: 'Type', key: 'entity_type', width: '100px' },
        { title: 'Entity ID', key: 'entity_id', width: '100px' },
        { title: 'Client ID', key: 'client_id', width: '100px' },
        { title: 'Reason', key: 'reason' },
        { title: 'Deleted By', key: 'deleted_by_user_id', width: '100px' },
        { title: 'Deleted At', key: 'deleted_at', width: '180px' },
      ]
    }
  },
  methods: {
    async doSearch() {
      if (!this.searchValue || this.searchValue.trim() === '') return;

      this.loading = true;
      this.errorMessage = '';
      this.lastSearchValue = this.searchValue.trim();
      this.lastSearchType = this.searchTypes.find(t => t.value === this.searchType)?.text || this.searchType;

      try {
        const result = await searchGdprDeletions(this.token, this.searchValue.trim(), this.searchType);
        log("GDPR search result:");
        log(result);

        if (result.error) {
          this.errorMessage = result.message;
          this.results = [];
        } else {
          this.results = result.gdpr_deletions || [];
        }
        this.searchPerformed = true;
      } catch (err) {
        this.errorMessage = err.message || 'Search failed';
        this.results = [];
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString('ro-RO', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      });
    }
  },
  created() {
    this.token = store.getters.getToken;
  }
});
</script>
